import React, { useState, useEffect } from 'react';
import { db } from '../firebase';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { getPromoterPayouts, Promoter, PromoterPayout } from '../services/promoterService';
import { Tenant } from '../services/tenantService';
import { motion, AnimatePresence } from 'motion/react';
import { 
  Banknote, 
  Copy, 
  Check, 
  LogOut, 
  Mail, 
  Phone, 
  ExternalLink,
  Building2,
  TrendingUp,
  Calendar,
  Lock
} from 'lucide-react';
import { formatCurrency } from '../lib/formatters';

export const PromoterPortal: React.FC = () => {
  // Authentication states
  const [promoCodeInput, setPromoCodeInput] = useState('');
  const [emailInput, setEmailInput] = useState('');
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [loginError, setLoginError] = useState<string | null>(null);

  // Active authenticated promoter
  const [activePromoter, setActivePromoter] = useState<Promoter | null>(null);
  const [referredClients, setReferredClients] = useState<Tenant[]>([]);
  const [payouts, setPayouts] = useState<PromoterPayout[]>([]);
  const [isLoadingData, setIsLoadingData] = useState(false);
  const [copiedLink, setCopiedLink] = useState(false);

  // Auto-login from sessionStorage on load
  useEffect(() => {
    const savedCode = sessionStorage.getItem('activePromoterCode');
    const savedEmail = sessionStorage.getItem('activePromoterEmail');
    if (savedCode && savedEmail) {
      attemptLogin(savedCode, savedEmail);
    }
  }, []);

  const attemptLogin = async (code: string, email: string) => {
    setIsLoggingIn(true);
    setLoginError(null);
    try {
      const cleanCode = code.trim().toUpperCase();
      const cleanEmail = email.trim().toLowerCase();

      // Query database for matching promoter
      const promotersRef = collection(db, 'promoters');
      const q = query(promotersRef, where('code', '==', cleanCode));
      const querySnapshot = await getDocs(q);

      if (querySnapshot.empty) {
        setLoginError('Could not find active promoter campaign with that code.');
        setIsLoggingIn(false);
        return;
      }

      let foundPromoter: Promoter | null = null;
      querySnapshot.forEach((docSnap) => {
        const data = docSnap.data();
        if (data.email.trim().toLowerCase() === cleanEmail) {
          foundPromoter = { id: docSnap.id, ...data } as Promoter;
        }
      });

      if (!foundPromoter) {
        setLoginError('The promo code and email combination is incorrect.');
        setIsLoggingIn(false);
        return;
      }

      // Login success
      setActivePromoter(foundPromoter);
      sessionStorage.setItem('activePromoterCode', cleanCode);
      sessionStorage.setItem('activePromoterEmail', cleanEmail);

      // Load performance metrics
      await loadPromoterPerformanceData(foundPromoter);

    } catch (err) {
      console.error('Login error:', err);
      setLoginError('An unexpected server error occurred. Please try again.');
    } finally {
      setIsLoggingIn(false);
    }
  };

  const handleLoginSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!promoCodeInput || !emailInput) {
      setLoginError('Please supply both your unique Promo Code and registered email.');
      return;
    }
    attemptLogin(promoCodeInput, emailInput);
  };

  const handleSignOut = () => {
    setActivePromoter(null);
    setReferredClients([]);
    setPayouts([]);
    sessionStorage.removeItem('activePromoterCode');
    sessionStorage.removeItem('activePromoterEmail');
  };

  const loadPromoterPerformanceData = async (promoter: Promoter) => {
    setIsLoadingData(true);
    try {
      // 1. Fetch referred clients (tenants with referredBy === promoter.code)
      const tenantsRef = collection(db, 'tenants');
      const tenantQuery = query(tenantsRef, where('referredBy', '==', promoter.code));
      const tenantSnap = await getDocs(tenantQuery);
      
      const clientList: Tenant[] = [];
      tenantSnap.forEach((docSnap) => {
        clientList.push({ tenantId: docSnap.id, ...docSnap.data() } as Tenant);
      });
      setReferredClients(clientList);

      // 2. Fetch logged payouts
      const payoutList = await getPromoterPayouts(promoter.id);
      setPayouts(payoutList);
    } catch (err) {
      console.error('Error fetching promoter data:', err);
    } finally {
      setIsLoadingData(false);
    }
  };

  // Helper calculation formulas (matching SuperAdminPortal logic)
  const getClientMonthlySubscription = (planId: string) => {
    if (planId === 'enterprise') return 499;
    if (planId === 'enhanced') return 199;
    return 99; // base plan
  };

  const pct = activePromoter && typeof activePromoter.commissionPercentage === 'number' 
    ? activePromoter.commissionPercentage 
    : 20;

  // Active monthly MRR referrals generated
  const activeMRRCommission = referredClients.reduce((acc, c) => {
    const subRate = getClientMonthlySubscription(c.planId || 'base');
    return acc + (subRate * (pct / 100));
  }, 0);

  // Cumulative earnings since start date
  const totalEarnedCommissions = referredClients.reduce((acc, c) => {
    let monthsActive = 1;
    if (c.createdAt) {
      try {
        let createdTime = NaN;
        if (c.createdAt && typeof c.createdAt.toDate === 'function') {
          createdTime = c.createdAt.toDate().getTime();
        } else if (c.createdAt && typeof c.createdAt.seconds === 'number') {
          createdTime = c.createdAt.seconds * 1000;
        } else {
          createdTime = new Date(c.createdAt).getTime();
        }
        if (!isNaN(createdTime)) {
          const msPassed = new Date().getTime() - createdTime;
          monthsActive = Math.max(1, Math.round(msPassed / (1000 * 60 * 60 * 24 * 30)));
        }
      } catch (e) {
        monthsActive = 1;
      }
    }
    const rate = getClientMonthlySubscription(c.planId || 'base');
    return acc + (rate * (pct / 100) * monthsActive);
  }, 0);

  const totalPaid = activePromoter?.totalPaidCommissions || 0;
  const pendingDue = Math.max(0, totalEarnedCommissions - totalPaid);

  const getSharingLink = () => {
    return `https://tipvalue.com/?code=${activePromoter?.code}`;
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(getSharingLink());
    setCopiedLink(true);
    setTimeout(() => setCopiedLink(false), 2000);
  };

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col justify-between selection:bg-indigo-500 selection:text-white">
      
      {/* HEADER BAR */}
      <header className="bg-white border-b border-gray-100 py-4 px-6 shadow-xs sticky top-0 z-10">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <div className="flex items-center gap-2">
            <div className="bg-indigo-600 p-2 rounded-xl text-white">
              <TrendingUp className="w-4.5 h-4.5" />
            </div>
            <div>
              <span className="text-[10px] font-black text-indigo-650 uppercase tracking-widest block">Grassroots Partner Core</span>
              <h1 className="text-base font-black text-slate-900 tracking-tight leading-none mt-0.5">GrowEzy Affiliate Dash</h1>
            </div>
          </div>

          {activePromoter && (
            <button
              onClick={handleSignOut}
              className="flex items-center gap-1.5 px-3 py-1.5 border border-gray-200 hover:border-rose-200 text-gray-600 hover:text-rose-600 font-extrabold text-[10px] uppercase rounded-xl transition-all cursor-pointer"
            >
              <LogOut className="w-3.5 h-3.5" /> Sign Out
            </button>
          )}
        </div>
      </header>

      {/* CORE PORTAL CONTENT */}
      <main className="grow max-w-7xl w-full mx-auto px-4 md:px-6 py-4 md:py-8 flex flex-col justify-center">
        
        <AnimatePresence mode="wait">
          {!activePromoter ? (
            /* LOGIN SCREEN */
            <motion.div 
              key="login"
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -15 }}
              className="max-w-md w-full mx-auto bg-white p-6 sm:p-8 rounded-3xl border border-gray-150 shadow-xl space-y-6"
            >
              <div className="text-center space-y-2">
                <div className="w-12 h-12 bg-indigo-50 text-indigo-600 rounded-2xl flex items-center justify-center mx-auto shadow-inner">
                  <Lock className="w-5 h-5" />
                </div>
                <div>
                  <h2 className="text-lg font-black text-slate-900">Partner Access Portal</h2>
                  <p className="text-xs text-gray-500 mt-1">Provide your registered email and unique Promo Code to access your real-time analytics & rewards ledger.</p>
                </div>
              </div>

              {loginError && (
                <div className="p-3.5 bg-rose-50 border border-rose-100 rounded-xl text-rose-700 text-xs font-semibold leading-relaxed">
                  {loginError}
                </div>
              )}

              <form onSubmit={handleLoginSubmit} className="space-y-4">
                <div className="space-y-1">
                  <label className="text-[10px] font-bold text-gray-500 uppercase tracking-wider block">Affiliate Promo Code</label>
                  <input
                    type="text"
                    required
                    value={promoCodeInput}
                    onChange={(e) => setPromoCodeInput(e.target.value)}
                    placeholder="e.g., GROW-BOB"
                    className="w-full h-12 px-4 border border-gray-250 rounded-xl text-base md:text-sm font-black text-indigo-800 uppercase tracking-wider focus:ring-2 focus:ring-indigo-150 focus:border-indigo-500 focus:outline-none transition-all"
                  />
                </div>

                <div className="space-y-1">
                  <label className="text-[10px] font-bold text-gray-500 uppercase tracking-wider block">Registered Email Address</label>
                  <input
                    type="email"
                    required
                    value={emailInput}
                    onChange={(e) => setEmailInput(e.target.value)}
                    placeholder="e.g., partner@website.com"
                    className="w-full h-12 px-4 border border-gray-250 rounded-xl text-base md:text-sm font-semibold focus:ring-2 focus:ring-indigo-150 focus:border-indigo-500 focus:outline-none transition-all"
                  />
                </div>

                <button
                  type="submit"
                  disabled={isLoggingIn}
                  className="w-full h-12 bg-slate-900 hover:bg-slate-800 text-white font-black text-xs uppercase tracking-widest rounded-xl transition-all shadow-md disabled:opacity-50 flex items-center justify-center gap-2 cursor-pointer touch-none select-none"
                >
                  {isLoggingIn ? 'Fetching Ledger...' : 'Access Dashboard'}
                </button>
              </form>

              <div className="text-center pt-1">
                <p className="text-[10px] text-gray-400">
                  Earn a <strong>{pct}% lifetime recurring commission</strong> on subscription fees for every venue client referred.
                </p>
              </div>
            </motion.div>
          ) : (
            /* DASHBOARD SCREEN */
            <motion.div 
              key="dashboard"
              initial={{ opacity: 0, scale: 0.98 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0 }}
              className="space-y-5"
            >
              {/* Profile Card & Link Copy Builder */}
              <div className="bg-gradient-to-br from-indigo-950 via-slate-900 to-indigo-900 text-white p-5 sm:p-8 rounded-[2rem] shadow-xl border border-indigo-950 flex flex-col lg:flex-row lg:items-center justify-between gap-6">
                <div className="space-y-2.5">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="bg-indigo-500/35 border border-indigo-400/30 text-indigo-200 text-[10px] font-extrabold uppercase tracking-widest px-2.5 py-0.5 rounded-full">
                      Affiliate Campaign Partner
                    </span>
                    <span className="bg-amber-500/25 border border-amber-400/20 text-amber-200 text-[10px] font-extrabold uppercase tracking-wide px-2.5 py-0.5 rounded-full">
                      Payout Rate: {pct}%
                    </span>
                  </div>
                  <h2 className="text-xl sm:text-2xl font-black text-white tracking-tight leading-none">Hello, {activePromoter.name}!</h2>
                  <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 text-xs font-semibold text-indigo-200">
                    <span className="flex items-center gap-1.5"><Mail className="w-3.5 h-3.5 text-indigo-300" /> {activePromoter.email}</span>
                    {activePromoter.phone && <span className="flex items-center gap-1.5"><Phone className="w-3.5 h-3.5 text-indigo-300" /> {activePromoter.phone}</span>}
                    <span className="inline-flex max-w-max items-center gap-1.5 bg-white/10 px-2 py-0.5 rounded text-white text-[10px] font-mono">CODE: {activePromoter.code}</span>
                  </div>
                  <p className="text-[11px] text-indigo-200/80 italic leading-relaxed">
                    Campaign Channel: {activePromoter.referralMethod} {activePromoter.referralMethodDetails ? `• "${activePromoter.referralMethodDetails}"` : ''}
                  </p>
                </div>

                {/* Sharing Link Box */}
                <div className="bg-white/5 border border-white/10 p-4 sm:p-5 rounded-2xl lg:max-w-md w-full space-y-3">
                  <div className="space-y-1">
                    <span className="text-[9px] font-black uppercase text-indigo-300 tracking-wider flex items-center gap-1">
                      Your Unique Referral Code Link
                    </span>
                    <p className="text-[10px] text-indigo-200/70 font-semibold leading-relaxed">
                      Share this custom partner URL with hospitality operators to associate their accounts with your campaign.
                    </p>
                  </div>

                  <div className="h-11 bg-black/35 border border-white/10 px-4 rounded-xl flex items-center justify-between text-xs font-mono select-all">
                    <span className="text-gray-300 select-all truncate shrink">{getSharingLink()}</span>
                  </div>

                  <button
                    onClick={handleCopyLink}
                    className="w-full h-11 bg-indigo-600 hover:bg-indigo-500 text-white font-black text-xs uppercase tracking-wider rounded-xl transition-all shadow-md flex items-center justify-center gap-1.5 cursor-pointer touch-none select-none"
                  >
                    {copiedLink ? (
                      <>
                        <Check className="w-4 h-4 text-amber-300 animate-bounce" />
                        Copied Successfully!
                      </>
                    ) : (
                      <>
                        <Copy className="w-3.5 h-3.5" />
                        Copy Referral Link
                      </>
                    )}
                  </button>
                </div>
              </div>

              <div className="pt-4">
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6">
                  {/* Metric 1 */}
                  <div className="bg-white p-4 sm:p-6 rounded-2xl border border-gray-150 shadow-xs space-y-2">
                    <span className="text-[9px] sm:text-[10px] uppercase font-black text-gray-400 block tracking-wider">Client Venues</span>
                    <div className="text-xl sm:text-3xl font-black text-slate-800">{isLoadingData ? '...' : referredClients.length}</div>
                    <p className="text-[9px] text-gray-500 font-bold leading-tight hidden sm:block">Venues linked & active on subscriptions</p>
                  </div>

                  {/* Metric 2 */}
                  <div className="bg-white p-4 sm:p-6 rounded-2xl border border-gray-150 shadow-xs space-y-2">
                    <span className="text-[9px] sm:text-[10px] uppercase font-black text-gray-400 block tracking-wider">Active Monthly Share</span>
                    <div className="text-xl sm:text-3xl font-black text-indigo-700">{isLoadingData ? '...' : formatCurrency(activeMRRCommission)}</div>
                    <p className="text-[9px] text-indigo-500 font-bold leading-tight hidden sm:block">Your recurring {pct}% share of subscribers</p>
                  </div>

                  {/* Metric 3 */}
                  <div className="bg-white p-4 sm:p-6 rounded-2xl border border-gray-150 shadow-xs space-y-2">
                    <span className="text-[9px] sm:text-[10px] uppercase font-black text-gray-400 block tracking-wider">Earned To-Date</span>
                    <div className="text-xl sm:text-3xl font-black text-emerald-600">{isLoadingData ? '...' : formatCurrency(totalEarnedCommissions)}</div>
                    <p className="text-[9px] text-emerald-600 font-bold leading-tight hidden sm:block">Cumulative commission generated</p>
                  </div>

                  {/* Metric 4 */}
                  <div className="bg-white p-4 sm:p-6 rounded-2xl border border-gray-150 shadow-xs space-y-2">
                    <span className="text-[9px] sm:text-[10px] uppercase font-black text-gray-400 block tracking-wider">Balance Pending Due</span>
                    <div className="text-xl sm:text-3xl font-black text-amber-600">{isLoadingData ? '...' : formatCurrency(pendingDue)}</div>
                    <p className="text-[9px] text-amber-600 font-bold leading-tight hidden sm:block">Balance due for payout transfer</p>
                  </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 mt-6 items-start">
                  {/* Left side: Referred clients list (8-span) */}
                  <div className="lg:col-span-8 bg-white border border-gray-200 rounded-2xl shadow-xs p-4 sm:p-6 space-y-4">
                    <div>
                      <h3 className="text-xs font-black text-slate-900 uppercase tracking-wider flex items-center gap-1.5">
                        <Building2 className="w-4 h-4 text-indigo-650" /> Referred Client Venues
                      </h3>
                      <p className="text-[10px] text-gray-500">Live feed of active registered hospitality venues connected with your promo code.</p>
                    </div>

                    {isLoadingData ? (
                      <div className="text-center py-12 text-xs text-indigo-600 font-bold animate-pulse">Synchronizing referred venue accounts...</div>
                    ) : referredClients.length === 0 ? (
                      <div className="text-center py-12 border border-dashed border-gray-200 bg-slate-50/50 rounded-2xl text-gray-400 text-xs font-bold">
                        No venues registered yet using your referral link/code.
                      </div>
                    ) : (
                      <div className="overflow-x-auto">
                        <table className="w-full text-left border-collapse">
                          <thead>
                            <tr className="border-b border-gray-150 text-[10px] font-black uppercase text-gray-400 tracking-wider">
                              <th className="pb-3 px-4">Venue Details</th>
                              <th className="pb-3 px-4">Signup Date</th>
                              <th className="pb-3 px-4">Pricing Tier</th>
                              <th className="pb-3 px-4">Subscription Rate</th>
                              <th className="pb-3 px-4 text-right">Your Share</th>
                            </tr>
                          </thead>
                          <tbody>
                            {referredClients.map(c => {
                              const rate = getClientMonthlySubscription(c.planId || 'base');
                              const myShare = rate * (pct / 100);
                              
                              let formattedDate = 'Recent';
                              if (c.createdAt) {
                                try {
                                  if (c.createdAt.toDate) {
                                    formattedDate = c.createdAt.toDate().toLocaleDateString();
                                  } else {
                                    formattedDate = new Date(c.createdAt).toLocaleDateString();
                                  }
                                } catch (e) {
                                  formattedDate = 'Recent';
                                }
                              }

                              return (
                                <tr key={c.tenantId} className="border-b border-gray-100 last:border-b-0 text-xs font-semibold hover:bg-slate-50/50 transition-all">
                                  <td className="py-3.5 px-4">
                                    <div className="font-extrabold text-slate-800 flex items-center gap-1.5">
                                      {c.name || 'Unnamed Venue'}
                                      <a href={`https://${c.subdomain}.tipvalue.com`} target="_blank" rel="noreferrer" className="text-gray-400 hover:text-indigo-650 shrink-0">
                                        <ExternalLink className="w-3 h-3" />
                                      </a>
                                    </div>
                                    <span className="text-[9px] text-gray-400 block mt-0.5 font-mono">{c.subdomain}.tipvalue.com</span>
                                  </td>
                                  <td className="py-3.5 px-4 text-gray-500 font-medium">
                                    {formattedDate}
                                  </td>
                                  <td className="py-3.5 px-4">
                                    <span className="px-2 py-0.5 bg-indigo-50 border border-indigo-150 text-indigo-700 font-extrabold text-[9px] rounded uppercase">
                                      {c.planId ? c.planId.toUpperCase() : 'BASE'}
                                    </span>
                                  </td>
                                  <td className="py-3.5 px-4 text-gray-800">
                                    {formatCurrency(rate)}/mo
                                  </td>
                                  <td className="py-3.5 px-4 text-right font-black text-indigo-700 text-xs">
                                    +{formatCurrency(myShare)}/mo
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>

                  {/* Right side: Payout history ledger */}
                  <div className="lg:col-span-4 bg-white border border-gray-200 rounded-2xl shadow-xs p-4 sm:p-6 space-y-4">
                    <div>
                      <h3 className="text-xs font-black text-slate-900 uppercase tracking-wider flex items-center gap-1.5">
                        <Banknote className="w-4 h-4 text-indigo-650" /> Logged Payouts History
                      </h3>
                      <p className="text-[10px] text-gray-500">Official cash transfers/payouts processed and logged by the App Owner.</p>
                    </div>

                    {isLoadingData ? (
                      <div className="text-center py-6 text-xs text-gray-400 font-medium animate-pulse">Syncing payout lines...</div>
                    ) : payouts.length === 0 ? (
                      <div className="text-center py-8 border border-dashed border-gray-200 bg-slate-50/50 rounded-xl text-gray-400 text-[10px] font-bold">
                        No payout logs available yet. Outstanding balance will show as unpaid ledger due.
                      </div>
                    ) : (
                      <div className="space-y-3.5 max-h-[300px] overflow-y-auto pr-1">
                        {payouts.map(p => (
                          <div key={p.id} className="p-3 bg-slate-50 border border-gray-150 rounded-xl space-y-1">
                            <div className="flex justify-between items-center text-xs font-black">
                              <span className="text-slate-800">{formatCurrency(p.amount)}</span>
                              <span className="text-gray-400 text-[9px] font-semibold flex items-center gap-0.5">
                                <Calendar className="w-3 h-3" /> {new Date(p.date).toLocaleDateString()}
                              </span>
                            </div>
                            {p.notes && (
                              <p className="text-[10px] text-gray-500 italic font-semibold leading-normal">&ldquo;{p.notes}&rdquo;</p>
                            )}
                          </div>
                        ))}
                      </div>
                    )}

                    <div className="bg-slate-900 text-slate-100 p-4 rounded-xl border border-slate-800 text-[10px] leading-relaxed font-semibold">
                      <p className="font-extrabold uppercase text-[9px] text-amber-400 tracking-wider">Need account changes?</p>
                      <p className="mt-1 text-slate-300">If your payment account email or phone details require modification, please communicate directly with the GrowEzy system owner.</p>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

      </main>

      {/* FOOTER */}
      <footer className="bg-white border-t border-gray-150 py-4 px-6 text-center text-[10px] text-gray-400 font-semibold font-mono tracking-wide mt-12 shrink-0">
        <div>GrowEzy Partner Ecosystem Portals © {new Date().getFullYear()}</div>
        <div className="mt-0.5">Safe, Secure & Transacted over Cloud SSL</div>
      </footer>

    </div>
  );
};
